import { forwardRef, useEffect, useMemo, useState } from "react";
import { submitAdvancedMedicalInfo } from "../../api/forms/medical-info";
import { AdvancedMedicalInfo } from "../../api/forms/medical-info/types";
import { Nullable } from "../../api/types";
import useDataInputForm, { FORM_IDS } from "../../hooks/useDataInputForm";
import useImperativeFormSubmit from "../../hooks/useImperativeFormSubmit";
import { FormHandle, InputTypes } from "../../types";
import { onFormValuesChange } from "../../utils";
import Error from "../Error";
import DataInputGenericForm, {
    DataInputGenericFormProps,
} from "./DataInputGenericForm";

export default forwardRef<FormHandle<Nullable<AdvancedMedicalInfo>> | null, {}>(
    function AdvancedMedicalInfoForm(
        props,
        ref: React.ForwardedRef<FormHandle<
            Nullable<AdvancedMedicalInfo>
        > | null>
    ) {
        const { formData, loading, error, setFromData } = useDataInputForm({
            formId: FORM_IDS.ADVANED_MEDICAL_INFO,
        });

        const [formValues, setFormValues] =
            useState<Nullable<AdvancedMedicalInfo>>();

        useEffect(() => {
            if (formData) {
                setFormValues(formData as AdvancedMedicalInfo);
            }
        }, [formData]);

        const { valuesErrors, submissionLoading } =
            useImperativeFormSubmit<AdvancedMedicalInfo>({
                formValues,
                formData,
                ref,
                onSubmitSuccess: setFromData,
                submitFunction: submitAdvancedMedicalInfo,
            });

        type FormFields =
            DataInputGenericFormProps<AdvancedMedicalInfo>["formFields"];

        const formFields = useMemo<FormFields>(
            (): FormFields =>
                !formValues || !formData
                    ? []
                    : [
                          {
                              id: "familyIllness",
                              label: "Family Illness",
                              value: formValues.familyIllness,
                              valueToDisplay:
                                  formValues.familyIllness.join(", "),
                              originalValue: formData.familyIllness,
                              type: InputTypes.SELECT,
                              options: formData.allFamilyIllness,
                              multiple: true,
                              error: valuesErrors?.familyIllness,
                              required: false,
                          },
                      ],
            [formValues, formData, valuesErrors]
        );

        if (error) {
            return <Error />;
        }

        return (
            <DataInputGenericForm
                formTitle="Additional Medical Information"
                formFields={formFields}
                formData={formValues}
                onFromValuesChange={({ value, fieldSettings }) =>
                    onFormValuesChange({
                        value,
                        fieldSettings,
                        setFormValues,
                    })
                }
                loading={loading || submissionLoading}
            />
        );
    }
);
